/**
 * SkillDelivery Platform — Skill.Civil Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Skill.Civil case-study detail view. Sourced from V2 Supabase
 * (case_studies WHERE domain='civil' AND slug=:slug).
 *
 * Renders Civil-specific sections (firms, generations, sectors, projects,
 * values quote, honest-AI framing) from the outcomes jsonb column. The
 * generic WMS CaseStudyDetail is untouched — Civil has its own detail
 * page because the WMS shape (challenge/solution/outcomes-table/tech-chips)
 * does not fit a family-legacy narrative.
 *
 * Public-safety: no phone, no email, no personal contact rendered. The
 * values statement is rendered verbatim from the DB (never templated).
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import MetaBadge from '../../components/showcase/MetaBadge';
import v2Supabase from '../../lib/v2Supabase';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
      }}
    />
  </>
);

const SectionHeading = ({ children }) => (
  <h2 className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan mt-12 mb-3">
    {children}
  </h2>
);

const Prose = ({ text }) => {
  if (!text) return null;
  return (
    <p className="text-base text-brand-text2 leading-relaxed whitespace-pre-line">
      {text}
    </p>
  );
};

// Dotted Skill.Civil wordmark used in the eyebrow.
const SkillCivilWordmark = () => (
  <>
    Skill<span className="text-brand-blue">.</span>Civil
  </>
);

// Firms table — 3 rows.
const FirmsTable = ({ firms }) => {
  if (!Array.isArray(firms) || firms.length === 0) return null;
  return (
    <div className="overflow-hidden border border-brand-border rounded-brandCard bg-white">
      <table className="w-full text-sm">
        <thead className="bg-brand-bgOff">
          <tr className="text-left">
            <th className="px-4 py-3 font-bold text-brand-text">Firm</th>
            <th className="px-4 py-3 font-bold text-brand-text">Partner(s)</th>
            <th className="px-4 py-3 font-bold text-brand-text whitespace-nowrap">Generation</th>
          </tr>
        </thead>
        <tbody>
          {firms.map((f, i) => (
            <tr
              key={f.name || i}
              className={i % 2 === 1 ? 'bg-brand-bgOff/50' : ''}
            >
              <td className="px-4 py-3 font-semibold text-brand-text">{f.name}</td>
              <td className="px-4 py-3 text-brand-text2">{f.partners}</td>
              <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{f.generation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Three generations — vertical list with member + role.
const GenerationsList = ({ generations }) => {
  if (!Array.isArray(generations) || generations.length === 0) return null;

  // Group by generation to render clustered rows.
  const grouped = generations.reduce((acc, g) => {
    const key = g.gen || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(g);
    return acc;
  }, {});

  const genOrder = ['1st', '2nd', '3rd'];
  const keys = [
    ...genOrder.filter((k) => grouped[k]),
    ...Object.keys(grouped).filter((k) => !genOrder.includes(k)),
  ];

  return (
    <div className="space-y-6">
      {keys.map((gen) => (
        <div key={gen} className="bg-white border border-brand-border rounded-brandCard p-5">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan mb-3">
            {gen} generation
          </p>
          <div className="space-y-3">
            {grouped[gen].map((g, i) => (
              <div key={i}>
                <p className="font-semibold text-brand-text">{g.member}</p>
                <p className="text-sm text-brand-muted leading-relaxed">{g.role}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Departments & sectors — chip row.
const SectorChips = ({ sectors }) => {
  if (!Array.isArray(sectors) || sectors.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {sectors.map((s) => (
        <span
          key={s}
          className="inline-block rounded-brandPill bg-brand-cyan/10 text-brand-cyanDark border border-brand-cyan/30 px-3 py-1 text-xs font-semibold"
        >
          {s}
        </span>
      ))}
    </div>
  );
};

// 16 projects table.
const ProjectsTable = ({ projects, intro }) => {
  if (!Array.isArray(projects) || projects.length === 0) return null;
  return (
    <>
      {intro && (
        <p className="text-sm text-brand-muted italic mb-3">{intro}</p>
      )}
      <div className="overflow-x-auto border border-brand-border rounded-brandCard bg-white">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-brand-bgOff">
            <tr className="text-left">
              <th className="px-3 py-3 font-bold text-brand-text w-10">#</th>
              <th className="px-3 py-3 font-bold text-brand-text">Project</th>
              <th className="px-3 py-3 font-bold text-brand-text whitespace-nowrap">Client / Dept</th>
              <th className="px-3 py-3 font-bold text-brand-text whitespace-nowrap">Location</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p, i) => (
              <tr
                key={p.n || i}
                className={i % 2 === 1 ? 'bg-brand-bgOff/50' : ''}
              >
                <td className="px-3 py-3 text-brand-muted2 tabular-nums">{p.n}</td>
                <td className="px-3 py-3 text-brand-text">{p.description}</td>
                <td className="px-3 py-3 text-brand-text2 whitespace-nowrap">{p.client}</td>
                <td className="px-3 py-3 text-brand-muted whitespace-nowrap">{p.location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Verbatim values quote block. Text comes straight from the DB — this
// component renders it inside a bordered card and never templates or
// transforms it.
const ValuesQuote = ({ heading, statement }) => {
  if (!statement) return null;
  return (
    <div className="mt-6 bg-white border-l-4 border-brand-cyan rounded-r-brandCard p-6 sm:p-8 shadow-brand">
      {heading && (
        <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan mb-4">
          {heading}
        </p>
      )}
      <blockquote className="text-lg sm:text-xl italic text-brand-text leading-relaxed">
        {statement}
      </blockquote>
    </div>
  );
};

const CivilCaseStudyDetail = () => {
  const { slug } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ok | not-found | error
  const [study, setStudy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('case_studies')
        .select('*')
        .eq('domain', 'civil')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle()
        .execute();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[CivilCaseStudyDetail] load failed:', error);
        setStatus('error');
        return;
      }
      if (!data) {
        setStatus('not-found');
        return;
      }
      setStudy(data);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // --- Loading / error / 404 frames ---------------------------------------
  if (status === 'loading') {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
        <PublicNav />
        <BrandGlows />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-16">
          <div className="mt-10 animate-pulse">
            <div className="h-4 w-32 bg-brand-bgOff2 rounded mb-10" />
            <div className="h-10 bg-brand-bgOff2 rounded w-3/4 mb-4" />
            <div className="h-5 bg-brand-bgOff2 rounded w-1/2 mb-8" />
            <div className="space-y-3">
              <div className="h-3 bg-brand-bgOff2 rounded" />
              <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
              <div className="h-3 bg-brand-bgOff2 rounded w-4/6" />
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (status === 'not-found' || status === 'error') {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
        <PublicNav />
        <BrandGlows />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 sm:pt-20 pb-16 text-center">
          <div className="mt-16 text-5xl mb-4" aria-hidden="true">
            {status === 'not-found' ? '🔍' : '⚠️'}
          </div>
          <h1 className="text-3xl font-bold text-brand-text">
            {status === 'not-found' ? 'Case study not found' : "Couldn't load case study"}
          </h1>
          <p className="mt-3 text-base text-brand-muted">
            {status === 'not-found'
              ? `We couldn't find a Civil case study with slug "${slug}".`
              : 'Please refresh the page and try again.'}
          </p>
          <div className="mt-8">
            <Link
              to="/domain/civil/case-studies"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 transition-all duration-200"
            >
              ← All Civil case studies
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // --- Loaded ---------------------------------------------------------------
  const outcomes = study.outcomes || {};

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <article className="relative max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* Back link */}
        <div className="mt-10">
          <Link
            to="/domain/civil/case-studies"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← All Civil case studies
          </Link>
        </div>

        {/* Hero */}
        <header className="mt-8">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            <SkillCivilWordmark /> · Case Study
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tighter leading-tight text-brand-text">
            {study.title}
          </h1>
          {study.subtitle && (
            <p className="mt-3 text-lg text-brand-text2 leading-relaxed">
              {study.subtitle}
            </p>
          )}
          {study.client_descriptor && (
            <p className="mt-4 text-base italic text-brand-muted">
              {study.client_descriptor}
            </p>
          )}

          {/* Meta badges */}
          <div className="mt-5 flex flex-wrap gap-1.5">
            <MetaBadge value={study.industry} label="Industry" variant="cyan" />
            <MetaBadge value={study.region} label="Region" variant="blue" />
            <MetaBadge value={study.years} label="Years" variant="muted" />
            <MetaBadge value={study.role} label="Role" variant="muted" />
          </div>
        </header>

        {/* Summary */}
        {study.summary && (
          <>
            <SectionHeading>Summary</SectionHeading>
            <Prose text={study.summary} />
          </>
        )}

        {/* Firms */}
        {Array.isArray(outcomes.firms) && outcomes.firms.length > 0 && (
          <>
            <SectionHeading>The Firms</SectionHeading>
            <FirmsTable firms={outcomes.firms} />
          </>
        )}

        {/* Three generations */}
        {Array.isArray(outcomes.generations) && outcomes.generations.length > 0 && (
          <>
            <SectionHeading>Three Generations</SectionHeading>
            <GenerationsList generations={outcomes.generations} />
          </>
        )}

        {/* Departments & sectors */}
        {Array.isArray(outcomes.departments_and_sectors) && outcomes.departments_and_sectors.length > 0 && (
          <>
            <SectionHeading>Departments & Sectors</SectionHeading>
            <SectorChips sectors={outcomes.departments_and_sectors} />
          </>
        )}

        {/* Projects */}
        {Array.isArray(outcomes.projects) && outcomes.projects.length > 0 && (
          <>
            <SectionHeading>
              Projects ({outcomes.projects.length})
            </SectionHeading>
            <ProjectsTable
              projects={outcomes.projects}
              intro={outcomes.projects_intro}
            />
          </>
        )}

        {/* Values quote — VERBATIM from DB. Do not template. */}
        {outcomes.values_statement && (
          <>
            <SectionHeading>Values</SectionHeading>
            <ValuesQuote
              heading={outcomes.values_heading}
              statement={outcomes.values_statement}
            />
          </>
        )}

        {/* Honest AI framing */}
        {outcomes.ai_framing && (
          <>
            <SectionHeading>Skill.Civil AI — honest framing</SectionHeading>
            <div className="bg-brand-bgOff border border-brand-border rounded-brandCard p-5 sm:p-6">
              <p className="text-base text-brand-text2 leading-relaxed">
                {outcomes.ai_framing}
              </p>
            </div>
          </>
        )}

        {/* CTA — early access, no phone/email surfaced */}
        <div className="mt-16 text-center bg-white border border-brand-border rounded-brandCard p-8">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Ready to see more?
          </p>
          <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Explore <SkillCivilWordmark />.
          </h3>
          <p className="mt-3 text-sm text-brand-muted max-w-xl mx-auto">
            SkillBot for Civil is being trained against decades of on-site
            practice. Try it, and see the Cricket Dashboard for Civil
            construction analytics.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/domain/civil"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200"
            >
              Explore Skill.Civil →
            </Link>
            <Link
              to="/domain/civil/case-studies"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 transition-all duration-200"
            >
              ← All Civil case studies
            </Link>
          </div>
        </div>

      </article>

      <PublicFooter />
    </div>
  );
};

export default CivilCaseStudyDetail;
